import { Component, OnDestroy, OnInit, ViewChild, inject } from "@angular/core";
import { AbstractControl, FormGroup } from "@angular/forms";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { MatDialogRef } from "@angular/material/dialog";
import { Utilities } from "@tylertech/shared-module";
import { LgdFormControlFactory, setEnabled } from "@tylertech/lgd-reactive-forms";
import { ToastService } from "@tylertech/core-module";
import { IntentService } from "@tylertech/intents-module";
import { VendorLookupService } from "@tylertech/accounts-payable-module";
import
{
        DepartmentViewModel,
        VendorLookupViewModel,
        RequisitionLookupViewModel,
        ICreatePurchaseOrdersFromRequisitionsCriteria,
        LookupCriteria,
        ExecutionResult,
        RequisitionSearchViewModel,
        Tuple,
    } from "@tylertech/tyler-api-types";
import { IReqDefaultEmailInfo } from "../../interfaces";
import { Command, LabelCommand } from "@tylertech/commands-module";
import { MatStepper } from "@angular/material/stepper";
import { HttpErrorResponse } from "@angular/common/http";
import { DepartmentLookupService, RequisitionLookupService, PurchaseOrderService } from "../../services";
const bulletChar = "\u2022";
@Component({
    selector: "create-purchase-order",
    templateUrl: "./createPurchaseOrderDialog.tmpl.html",
    styleUrls: ["./createPurchaseOrderDialog.component.scss"],
    standalone: false
})
export class RequisitionSelectionDialogComponent implements OnInit, OnDestroy
{
    private readonly matDialog = inject<MatDialogRef<RequisitionSelectionDialogComponent, void>>(MatDialogRef);
    private readonly $departmentService = inject(DepartmentLookupService);
    private readonly $vendorLookupService = inject(VendorLookupService);
    private readonly $requisitionLookupService = inject(RequisitionLookupService);
    private readonly $purchaseOrderService = inject(PurchaseOrderService);
    private readonly $intentService = inject(IntentService);
    private readonly $toastService = inject(ToastService);

    private readonly destructor$ = new Subject<void>();
    private readonly departmentsSubject = new BehaviorSubject<Array<DepartmentViewModel>>([]);
    private readonly vendorsSubject = new BehaviorSubject<Array<VendorLookupViewModel>>([]);
    private readonly requisitionsSubject = new BehaviorSubject<Array<RequisitionLookupViewModel>>([]);
    @ViewChild("stepper")
    private readonly matStepper: MatStepper;

    readonly selectionForm: FormGroup;
    readonly commands: ReadonlyArray<Command>;
    /** To/From RequisitionSelectionReview */ readonly issueDate: Date;
    /** To/From RequisitionSelectionReview */ selectedRequisitions: Array<RequisitionSearchViewModel>;
    /** To/From RequisitionSelectionReview */ get searchDepartment(): string
    {
        return this.department?.Description || "";
    }

    /** To/From RequisitionSelectionReview */ get searchVendor(): string
    {
        return this.vendor?.Name.split(bulletChar)[0].trim() || "";
    }

    /** To/From RequisitionSelectionReview */ get searchNumber(): string
    {
        return this.requisition?.Number || "";
    }

    /** To/From RequisitionSelectionReview */ get searchIssueDateFrom(): string
    {
        return this.selectedFromDate ? Utilities.toShortDateString(this.selectedFromDate) : "";
    }

    /** To/From RequisitionSelectionReview */ get searchIssueDateTo(): string
    {
        return this.selectedToDate ? Utilities.toShortDateString(this.selectedToDate) : "";
    }

    isProcessing: boolean;
    /** To/From poNoticeEmails */ private emailSubjectLine: string = null;
    /** To/From poNoticeEmails */ private emailBody: string = null;
    /** To/From poNoticeEmails */ private sendEmails: boolean;
    /** To/From poNoticeEmails */ selectedRequisitionIds: Array<string>;
    /** To/From poNoticeEmails */ defaultEmailReqInfo: Array<IReqDefaultEmailInfo>;
    constructor()
    {
        this.selectionForm = new FormGroup({
            allRequisitions: LgdFormControlFactory.checkbox(),
            department: LgdFormControlFactory.typeahead<DepartmentViewModel>(
                undefined,
                () => !this.selectAllRequisitions && !this.hasSpecificSelection,
                {
                    displayValueFn: item =>
                    {
                        if (!item?.Code && !item?.Description)
                        {
                            return "";
                        }
                        if (!item.Code)
                        {
                            return item.Description.trim();
                        }
                        if (!item.Description)
                        {
                            return item.Code.trim();
                        }
                        return `${item.Code.trim()} - ${item.Description.trim()}`;
                    },
                }
            ),
            fromDate: LgdFormControlFactory.date(undefined, false, {
                max: () => this.selectedToDate,
            }),
            toDate: LgdFormControlFactory.date(undefined, false, {
                min: () => this.selectedFromDate,
            }),
            vendor: LgdFormControlFactory.typeahead<VendorLookupViewModel>(
                undefined,
                () => !this.selectAllRequisitions && !this.hasMassSelection,
                {
                    displayValueFn: x => x?.Name,
                }
            ),
            requisition: LgdFormControlFactory.typeahead<RequisitionLookupViewModel>(
                undefined,
                () => !this.selectAllRequisitions && !this.hasMassSelection,
                {
                    displayValueFn: x => x?.Number,
                }
            ),
        });
        this.commands = [
            new LabelCommand({
                label: "Cancel",
                action: () => this.cancel(),
                getEnabled: () => true,
            }),
            new LabelCommand({
                label: "Back",
                action: () => this.back(),
                getEnabled: () => true,
                tooltip: undefined,
                getVisible: () => this.matStepper?.selectedIndex > 0,
            }),
            new LabelCommand({
                label: "Next",
                action: () => this.next(),
                getEnabled: () => this.selectionForm.valid,
                tooltip: undefined,
                getVisible: () => this.matStepper?.selectedIndex < 2,
            }),
            new LabelCommand({
                label: "Create PO",
                action: () => this.create(),
                getEnabled: () => this.hasSelection,
                tooltip: undefined,
                getVisible: () => this.matStepper?.selectedIndex === 2,
            }),
        ];
    }

    private get allRequisitionsControl(): AbstractControl<boolean> | undefined
    {
        return this.selectionForm?.get("allRequisitions");
    }

    private get departmentControl(): AbstractControl<DepartmentViewModel> | undefined
    {
        return this.selectionForm?.get("department");
    }

    private get fromDateControl(): AbstractControl<Date> | undefined
    {
        return this.selectionForm?.get("fromDate");
    }

    private get toDateControl(): AbstractControl<Date> | undefined
    {
        return this.selectionForm?.get("toDate");
    }

    private get vendorControl(): AbstractControl<VendorLookupViewModel> | undefined
    {
        return this.selectionForm?.get("vendor");
    }

    private get requisitionControl(): AbstractControl<RequisitionLookupViewModel> | undefined
    {
        return this.selectionForm?.get("requisition");
    }

    get departmentsObservable(): Observable<Array<DepartmentViewModel>>
    {
        return this.departmentsSubject.asObservable();
    }

    get vendorsObservable(): Observable<Array<VendorLookupViewModel>>
    {
        return this.vendorsSubject.asObservable();
    }

    get requisitionsObservable(): Observable<Array<RequisitionLookupViewModel>>
    {
        return this.requisitionsSubject.asObservable();
    }

    private get selectAllRequisitions(): boolean
    {
        return this.allRequisitionsControl?.value || false;
    }

    private get department(): DepartmentViewModel | undefined
    {
        return this.departmentControl?.value;
    }

    private get selectedFromDate(): Date | undefined
    {
        return this.fromDateControl?.value;
    }

    private get selectedToDate(): Date | undefined
    {
        return this.toDateControl?.value;
    }

    private get vendor(): VendorLookupViewModel | undefined
    {
        return this.vendorControl?.value;
    }

    private get requisition(): RequisitionLookupViewModel | undefined
    {
        return this.requisitionControl?.value;
    }

    private get hasMassSelection(): boolean
    {
        return !!this.department || !!this.selectedFromDate || !!this.selectedToDate;
    }

    private get hasSpecificSelection(): boolean
    {
        return !!this.vendor || !!this.requisition;
    }

    private get allowMassSelection(): boolean
    {
        return !this.selectAllRequisitions && !this.vendor && !this.requisition;
    }

    private get allowSpecificSelection(): boolean
    {
        return !this.selectAllRequisitions && !this.department && !this.selectedFromDate && !this.selectedToDate;
    }

    get hasSelection(): boolean
    {
        return this.selectedRequisitions?.length > 0;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        const self = this;
        const enableMassSelection = this.allowMassSelection;
        setEnabled(this.departmentControl, enableMassSelection);
        setEnabled(this.fromDateControl, enableMassSelection);
        setEnabled(this.toDateControl, enableMassSelection);
        const enabledSpecificSelection = this.allowSpecificSelection;
        setEnabled(this.vendorControl, enabledSpecificSelection);
        setEnabled(this.requisitionControl, enabledSpecificSelection);
        subscribeToValueChanges(this.allRequisitionsControl, (newValue: boolean) =>
        {
            if (newValue)
            {
                this.departmentControl.setValue(undefined);
                this.fromDateControl.setValue(undefined);
                this.toDateControl.setValue(undefined);
                this.vendorControl.setValue(undefined);
                this.requisitionControl.setValue(undefined);
            }
            const enableMassSelection = this.allowMassSelection;
            setEnabled(this.departmentControl, enableMassSelection, false, false);
            setEnabled(this.fromDateControl, enableMassSelection, false, false);
            setEnabled(this.toDateControl, enableMassSelection, false, false);
            const enabledSpecificSelection = this.allowSpecificSelection;
            setEnabled(this.vendorControl, enabledSpecificSelection, false, false);
            setEnabled(this.requisitionControl, enabledSpecificSelection, false, false);
        });
        subscribeToValueChanges(this.departmentControl, (_newValue: DepartmentViewModel) =>
        {
            const enabledSpecificSelection = this.allowSpecificSelection;
            setEnabled(this.vendorControl, enabledSpecificSelection, false, false);
            setEnabled(this.requisitionControl, enabledSpecificSelection, false, false);
        });
        subscribeToValueChanges(this.fromDateControl, (_newValue: Date) =>
        {
            const enabledSpecificSelection = this.allowSpecificSelection;
            setEnabled(this.vendorControl, enabledSpecificSelection, false, false);
            setEnabled(this.requisitionControl, enabledSpecificSelection, false, false);
        });
        subscribeToValueChanges(this.toDateControl, (_newValue: Date) =>
        {
            const enabledSpecificSelection = this.allowSpecificSelection;
            setEnabled(this.vendorControl, enabledSpecificSelection, false, false);
            setEnabled(this.requisitionControl, enabledSpecificSelection, false, false);
        });
        subscribeToValueChanges(this.vendorControl, (_newValue: VendorLookupViewModel) =>
        {
            const enableMassSelection = this.allowMassSelection;
            setEnabled(this.departmentControl, enableMassSelection, false, false);
            setEnabled(this.fromDateControl, enableMassSelection, false, false);
            setEnabled(this.toDateControl, enableMassSelection, false, false);
        });
        subscribeToValueChanges(this.requisitionControl, (_newValue: RequisitionLookupViewModel) =>
        {
            const enableMassSelection = this.allowMassSelection;
            setEnabled(this.departmentControl, enableMassSelection, false, false);
            setEnabled(this.fromDateControl, enableMassSelection, false, false);
            setEnabled(this.toDateControl, enableMassSelection, false, false);
        });
        function subscribeToValueChanges<T = unknown>(
            control: AbstractControl<T>,
            subscriptionFn: (newValue: T) => void
        ): void
        {
            control.valueChanges.pipe(takeUntil(self.destructor$)).subscribe(x =>
            {
                subscriptionFn(x);
            });
        }
    }

    private cancel(): void
    {
        this.matDialog.close();
    }

    private back(): void
    {
        this.matStepper.previous();
    }

    private next(): void
    {
        this.matStepper.next();
    }

    private create(): void
    {
        this.isProcessing = true;
        const crit: ICreatePurchaseOrdersFromRequisitionsCriteria = {
            RequisitionIds: this.selectedRequisitionIds,
            IssueDate: this.issueDate,
            EmailBody: this.emailBody,
            EmailSubject: this.emailSubjectLine,
            SendEmails: this.sendEmails,
        };
        this.$purchaseOrderService
            .CreatePurchaseOrdersFromRequisitions(crit, true)
            .then(
                (result: Tuple<{ [key: string]: string; }, string>) =>
                {
                    const joined: string = Object.values(result[0]).join(",");
                    this.$intentService.resolve("PurchaseOrder", "Search", { querystring: "?QueryString=" + joined });
                    this.matDialog.close();
                },
                (reason: HttpErrorResponse) =>
                {
                    this.$toastService.showToast(reason.error.Message);
                }
            )
            .finally(() =>
            {
                this.isProcessing = false;
            });
    }

    lookupDepartments(searchText: string): void
    {
        this.$departmentService
            .Lookup(<LookupCriteria>{ SearchText: searchText })
            .then((executionResult: ExecutionResult<Array<DepartmentViewModel>>) =>
            {
                this.departmentsSubject.next(executionResult.Result);
            });
    }

    lookupVendors(searchText: string): void
    {
        this.$vendorLookupService
            .Lookup(<LookupCriteria>{ SearchText: searchText })
            .then((executionResult: ExecutionResult<Array<VendorLookupViewModel>>) =>
            {
                this.vendorsSubject.next(executionResult.Result);
            });
    }

    lookupRequisitions($event: string): void
    {
        this.$requisitionLookupService
            .Lookup({
                SearchText: $event,
                Parameters: [
                    {
                        FieldId: "RequisitionsForPO",
                        Value: true,
                    },
                ],
            })
            .then((executionResult: ExecutionResult<Array<RequisitionLookupViewModel>>) =>
            {
                this.requisitionsSubject.next(executionResult.Result);
            });
    }

    onSelectionChanged($event: ReadonlyArray<RequisitionSearchViewModel>): void
    {
        this.selectedRequisitions = [...$event];
        this.selectedRequisitionIds = this.selectedRequisitions.map(v => v.Id);
        this.defaultEmailReqInfo = this.selectedRequisitions.map(
            (x: RequisitionSearchViewModel): IReqDefaultEmailInfo =>
            {
                return {
                    Description: x.Description,
                    Id: x.Id,
                    Number: x.Number,
                    Vendor: x.VendorName,
                    Emails: [],
                };
            }
        );
    }
}
